import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { OPTIONAL_SERVICES, PlanLimits, PlanSettings } from "@/models/package";

const LIMITS: { key: keyof PlanLimits; label: string; hint: string }[] = [
  { key: "teams", label: "Teams", hint: "Besides Administration. 0 = Administration only" },
  { key: "channels", label: "Channels", hint: "Any channel type" },
  { key: "aiTokensPerMonth", label: "AI tokens / month", hint: "Recorded only, not enforced yet" }
];

interface PlanSettingsFieldsProps {
  /** Keeps switch and input ids unique when several forms share a page. */
  idPrefix: string;
  value: PlanSettings;
  onChange: (next: PlanSettings) => void;
  disabled?: boolean;
}

/** The optional services and limits shared by packages and organization plans. */
export function PlanSettingsFields({
  idPrefix,
  value,
  onChange,
  disabled
}: PlanSettingsFieldsProps) {
  const toggleService = (service: PlanSettings["enabledServices"][number], on: boolean) =>
    onChange({
      ...value,
      enabledServices: on
        ? [...value.enabledServices, service]
        : value.enabledServices.filter((s) => s !== service)
    });

  const setLimit = (key: keyof PlanLimits, raw: string) =>
    onChange({
      ...value,
      limits: { ...value.limits, [key]: raw === "" ? null : Math.max(0, Math.floor(Number(raw))) }
    });

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        {OPTIONAL_SERVICES.map((service) => (
          <div key={service.value} className="flex items-start justify-between gap-4">
            <div>
              <Label htmlFor={`${idPrefix}-${service.value}`}>{service.label}</Label>
              <p className="text-xs text-muted-foreground">{service.description}</p>
            </div>
            <Switch
              id={`${idPrefix}-${service.value}`}
              checked={value.enabledServices.includes(service.value)}
              onCheckedChange={(on) => toggleService(service.value, on)}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {LIMITS.map((limit) => (
          <div key={limit.key} className="space-y-1">
            <Label htmlFor={`${idPrefix}-${limit.key}`}>{limit.label}</Label>
            <Input
              id={`${idPrefix}-${limit.key}`}
              type="number"
              min={0}
              step={1}
              placeholder="Unlimited"
              value={value.limits[limit.key] ?? ""}
              onChange={(e) => setLimit(limit.key, e.target.value)}
              disabled={disabled}
            />
            <p className="text-xs text-muted-foreground">{limit.hint}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
